import styles from './index.module.css'

import React from 'react'
import { IoClose } from '@react-icons/all-files/io5/IoClose'

import { getSearchMetadataKeys } from '@disco/state-management/selectors/search'

import { FilterComponentProps } from 'types/filter-page'

import { Button, Flex, Select } from 'components/ds'

import { useSelector } from 'hooks/use-redux'

const DropdownForm = ({
  label,
  value,
  onChange,
  operator = 'equals',
}: FilterComponentProps) => {
  // Get the metadata definitions from Redux to find dropdown values
  const searchMetadataKeys = useSelector(getSearchMetadataKeys)

  const fieldDefinition = searchMetadataKeys.find(
    key => key.text === label || key.key === label,
  )

  // Determine if this is a multi-select dropdown
  const isMultiSelect = fieldDefinition?.type === 'dropdown_multi'

  // Get dropdown options from the field definition values
  let options = fieldDefinition?.values || []

  // Convert current value to Select format
  const currentValues = value && Array.isArray(value) ? value : []

  const handleSelectChange = (newValue: string) => {
    if (isMultiSelect) {
      // For multi-select, add to existing values if not already present
      const updatedValues = currentValues.includes(newValue)
        ? currentValues
        : [...currentValues, newValue]
      onChange(updatedValues.length > 0 ? updatedValues : null, operator)
    } else {
      // For single select, replace the value
      const arrayValue = newValue ? [newValue] : null
      onChange(arrayValue, operator)
    }
  }

  const handleRemoveValue = (valueToRemove: string) => {
    const updatedValues = currentValues.filter(v => v !== valueToRemove)
    onChange(updatedValues.length > 0 ? updatedValues : null, operator)
  }

  // Computed values for unified component
  const availableOptions = isMultiSelect
    ? options.filter(option => !currentValues.includes(option))
    : options

  const selectValue = isMultiSelect ? '' : currentValues[0] || ''

  // Disable dropdown when all options are selected in multi-select mode
  const isDisabled = isMultiSelect && availableOptions.length === 0

  return (
    <div className={styles.dropdownFilter}>
      {/* Selected values - only shown for multi-select mode */}
      {isMultiSelect && currentValues.length > 0 && (
        <div className={styles.dropdownFilter__badgeContainer}>
          {currentValues.map(selectedValue => (
            <Button
              key={selectedValue}
              variant="soft"
              size="1"
              onClick={() => handleRemoveValue(selectedValue)}
            >
              {selectedValue}
              <IoClose />
            </Button>
          ))}
        </div>
      )}
      <Select.Root
        value={selectValue}
        onValueChange={handleSelectChange}
        disabled={isDisabled}
      >
        <Flex width="100%" asChild>
          <Select.Trigger
            placeholder={
              isDisabled ? 'All options selected' : 'Choose value...'
            }
          />
        </Flex>
        <Select.Content
          position="popper"
          side="bottom"
          align="start"
          sideOffset={4}
        >
          {availableOptions.map(option => (
            <Select.Item key={option} value={option}>
              {option}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
    </div>
  )
}

export default DropdownForm
